import { useEffect } from "react";

/**
 * Prev / next inside a modal swaps the body content but keeps the same DOM
 * node, so the scroll position of the old entry carries over and the reader
 * lands mid-way down the new one. Every unified modal footer uses the shared
 * `.ctb-modalNavBtn` class (see src/styles/modalKit.css), so one delegated
 * listener resets the scroll for all of them.
 */

const NAV_BTN_SELECTOR = ".ctb-modalNavBtn";

function isScrollable(el) {
  if (el.scrollHeight <= el.clientHeight + 1) return false;

  const { overflowY } = window.getComputedStyle(el);
  return overflowY === "auto" || overflowY === "scroll" || overflowY === "overlay";
}

/**
 * Walk up from the nav button until we reach the level that owns the
 * scrolling area — the modal dialog. The footer itself never scrolls, so the
 * first ancestor with scrollable descendants is the dialog.
 */
function findModalScrollers(button) {
  let node = button.parentElement;

  for (let depth = 0; node && node !== document.body && depth < 8; depth += 1) {
    const scrollers = Array.from(node.querySelectorAll("*")).filter(isScrollable);
    if (scrollers.length) return scrollers;
    node = node.parentElement;
  }

  return [];
}

function ModalNavScrollReset() {
  useEffect(() => {
    const handleClick = (event) => {
      const button = event.target.closest?.(NAV_BTN_SELECTOR);
      if (!button || button.disabled) return;

      const scrollers = findModalScrollers(button);
      if (!scrollers.length) return;

      const reset = () => {
        scrollers.forEach((el) => {
          el.scrollTop = 0;
          el.scrollLeft = 0;
        });
      };

      // Now, then again after React has painted the next entry.
      reset();
      window.requestAnimationFrame(() => {
        reset();
        window.requestAnimationFrame(reset);
      });
    };

    document.addEventListener("click", handleClick, true);
    return () => document.removeEventListener("click", handleClick, true);
  }, []);

  return null;
}

export default ModalNavScrollReset;
